import { tableDefaults, renderWordBoxes, renderClickableText } from "./defaultConfig.js";
import { hoursToTime } from "../utils/timeUtils.js";
import { deepLink, isResolvedName } from "../../../../app/utils/deepLink.js";

let dateFilterValue = null;

export const entryTableConfig = () => ({
    ...tableDefaults,
    rowFormatter: (row) => {
        const element = row.getElement();
        element.classList.toggle("day-off-row", !!row.getData().isDayOff);
    },
    columns: [
        {
            title: "",
            field: "",
            cssClass: "selectColumn",
            formatter: "rowSelection",
            titleFormatter: "rowSelection",
            hozAlign: "center",
            headerHozAlign: "center",
            headerSort: false,
            headerFilter: false,
            width: 40,
            minWidth: 40,
            widthGrow: 0,
            cellClick: (e, cell) => cell.getRow().toggleSelect(),
        },
        { title: "EntryID", field: "id", visible: false },
        {
            title: "Mitarbeiter",
            field: "userId",
            visible: false,
            headerFilter: "list",
            headerFilterParams: { values: [] },
            formatter: (cell) => {
                const data = cell.getRow().getData();
                const name = data.userName ?? "";
                if (!name) return "";
                return renderClickableText(name, deepLink('Users.Read', `#users/${data.userId}`, data.userId != null && isResolvedName(name)));
            },
        },
        {
            title: "Team",
            field: "teamId",
            visible: false,
            headerFilter: "list",
            headerFilterParams: { values: [] },
            formatter: (cell) => {
                const data = cell.getRow().getData();
                const name = data.teamName ?? "";
                if (!name) return "";
                return renderClickableText(name, deepLink('Teams.Read', `#teams?col-name=${encodeURIComponent(name)}`, data.teamId != null && isResolvedName(name)));
            },
            accessorDownload: (value, data) => data.teamName ?? "",
            sorter: (a, b, aRow, bRow) => (aRow.getData().teamName ?? "").localeCompare(bRow.getData().teamName ?? ""),
        },
        {
            title: "Kunde",
            field: "customerId",
            formatter: (cell) => {
                const data = cell.getRow().getData();
                const name = data.customerName ?? "";
                if (!name) return "";

                const content = renderClickableText(name, deepLink(
                    'Customers.Read',
                    `#customers/${data.customerId}`,
                    data.customerId != null && !data.customerIsDeleted && isResolvedName(name)
                ));
                if (!data.customerIsDeleted) return content;

                cell.getElement().classList.add("customer-deleted");

                const wrapper = document.createElement("span");
                wrapper.appendChild(content);
                const marker = document.createElement("span");
                marker.className = "deleted-marker";
                marker.textContent = "*";
                wrapper.appendChild(marker);
                return wrapper;
            },
        },
        {
            title: "Applikation",
            field: "applicationId",
            formatter: (cell) => {
                const data = cell.getRow().getData();
                const name = data.applicationName ?? "";
                if (!name) return "";
                return renderClickableText(name, deepLink('Applications.Read', `#applications?col-name=${encodeURIComponent(name)}`, data.applicationId != null && isResolvedName(name)));
            },
        },
        {
            title: "Projekt",
            field: "projectId",
            formatter: (cell) => {
                const row = cell.getRow().getData();
                if (row.projectId == null) return "";
                const name = row.projectName ?? "Unbekannt";
                return renderClickableText(name, deepLink('Projects.Read', `#projects?col-name=${encodeURIComponent(name)}`, isResolvedName(name)));
            },
            accessorDownload: (value, data) => {
                if (data.projectId == null) return "";
                return data.projectName ?? "Unbekannt";
            },
            sorter: (a, b, aRow, bRow) => {
                const aName = aRow.getData().projectId == null ? "" : (aRow.getData().projectName ?? "Unbekannt");
                const bName = bRow.getData().projectId == null ? "" : (bRow.getData().projectName ?? "Unbekannt");
                return aName.localeCompare(bName);
            },
        },
        { title: "Aktivität", field: "activityId" },
        {
            title: "Beschreibung",
            field: "description",
            widthGrow: 2,
            headerFilter: "input",
            headerFilterFunc: "like",
            sorter: "string",
        },
        {
            title: "Rechnungsträger",
            field: "payerId",
            formatter: (cell) => {
                const data = cell.getRow().getData();
                const name = data.payerName ?? "";
                if (!name) return "";
                return renderClickableText(name, deepLink('Customers.Read', `#customers/${data.payerId}`, data.payerId != null && isResolvedName(name)));
            },
        },
        {
            title: "Datum",
            field: "date",
            width: 130,
            minWidth: 130,
            hozAlign: "center",
            headerHozAlign: "center",
            headerFilter: (cell, onRendered, success) => {
                const element = document.createElement('app-input');
                element.setAttribute('type', 'datepicker');
                element.setAttribute('max', new Date().toISOString().slice(0, 10));
                element.style.cssText = 'width:100%';

                element.addEventListener('input-change', (event) => {
                    dateFilterValue = event.detail.value;
                    success(event.detail.value);
                });

                if (dateFilterValue) {
                    const observer = new MutationObserver(() => {
                        if (!element.hasAttribute('loaded')) return;
                        observer.disconnect();
                        element.setValue(dateFilterValue, { silent: true });
                    });
                    observer.observe(element, { attributes: true, attributeFilter: ['loaded'] });
                }

                return element;
            },
            headerFilterEmptyCheck: (value) => !value,
            headerFilterFunc: (headerVal, rowValue) => headerVal === rowValue,
            formatter: (cell) => {
                const value = cell.getValue();
                if (!value) return "";
                const [y, m, d] = value.split("-");
                return `${d}.${m}.${y}`;
            },
            accessorDownload: (value) => {
                if (!value) return "";
                const [y, m, d] = value.split("-").map(Number);
                return new Date(y, m - 1, d);
            },
            sorter: (a, b) => new Date(a) - new Date(b),
        },
        {
            title: "Stunden",
            field: "workedHours",
            width: 110,
            minWidth: 110,
            hozAlign: "center",
            headerHozAlign: "center",
            headerFilter: "input",
            headerFilterFunc: "like",
            sorter: "number",
            formatter: (cell) => {
                const value = cell.getValue();
                if (value === null || value === undefined) {
                    return '<app-loading-dots variant="running"></app-loading-dots>';
                }
                return hoursToTime(value);
            },
            accessorDownload: (value) => value,
        },
        {
            title: "Faktura",
            field: "isFactored",
            width: 100,
            minWidth: 100,
            cssClass: "statusColumn",
            hozAlign: "center",
            headerHozAlign: "center",
            headerFilter: "list",
            headerFilterParams: {
                values: { true: "Fakturiert", false: "Offen" },
            },
            formatter: (cell) => renderWordBoxes(cell.getValue() ? "Fakturiert;" : "Offen;"),
            accessorDownload: (value) => value ? "Fakturiert" : "Offen",
        }
    ],
});